import { PagedContractorSummaryReportResponse } from 'src/app/models/contractor-reports';
import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import {
  FilterBuilderOutput,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  Utilities,
  WorkflowContextService,
  DataService,
  ContractorService
} from 'src/app/services';
import { ComponentIds } from 'src/app/models/component-ids';
import { ColumnOption } from 'src/app/models/grid-settings';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-contractors-summary',
  templateUrl: './contractors-summary.component.html',
  styleUrls: ['./contractors-summary.component.css'],
  standalone: false
})
export class ContractorsSummaryComponent implements OnInit, OnChanges {
  id = ComponentIds.CONTRACTOR_SUMMARY;
  loading = true;
  title = 'Summary';
  errorMessage = '';
  report: PagedContractorSummaryReportResponse;
  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  params: FilterBuilderParam[] = [
    {
      name: 'Created Date',
      id: 'timeframe',
      inputType: 'date',
      types: ['range']
    }
  ];
  filters: FilterBuilderOutput = {};
  public useExcelFormat: boolean;
  public sortField: string;
  public sortDescending = false;
  lastQuery = {};
  rows: any[];
  public page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };

  timeFrame: FilterBuilderParam = {
    name: 'Paid Date',
    id: 'timeframe',
    inputType: 'date',
    types: ['range']
  };

  totals: any[];

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[];

  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-report-contractor-summary-janus';
  constructor(
    private contractorSvc: ContractorService,
    private dataSvc: DataService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.page.size = pageSize;
      }
    }

    this.columns = [
      {
        key: 'registrationType',
        label: 'Registration Type',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'pending',
        label: 'Pending',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'approved',
        label: 'Approved',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'denied',
        label: 'Denied',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'expired',
        label: 'Expired',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'inactive',
        label: 'Inactive',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'fees',
        label: 'Fees',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      }
    ];

    this.columnOptions = this.columns.map(c => ({
      key: c.key,
      name: c.key,
      label: c.label,
      checked: !c.hidden,
      includeInExport: c.showExportOption ?? true
    }));

    this.setAvailableColumns();
    // this.getReport();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  getQuery() {
    const timeframe = this.filters['timeframe'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  private getPagedQuery() {
    const timeframe = this.filters['timeframe'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  getReport() {
    const query = this.getPagedQuery();
    this.loading = true;
    this.contractorSvc.getPagedContractorSummaryReport(query).subscribe({
      next: report => {
        this.page.totalElements = report.recordCount;
        this.page.totalPages = Math.ceil(report.recordCount / this.page.size);
        this.rows = report.items.map(i => {
          i['registrationType'] = i.registrationType;
          i['pending'] = i.pending;
          i['approved'] = i.approved;
          i['denied'] = i.denied;
          i['expired'] = i.expired;
          i['inactive'] = i.inactive;
          i['fees'] = Utilities.formatCurrency(i.fees);
          return i;
        });
        this.report = report;
        this.totals = [
          { label: 'Approved', value: this.report.totalApproved },
          { label: 'Pending', value: this.report.totalPending },
          { label: 'Denied', value: this.report.totalDenied },
          { label: 'Inactive', value: this.report.totalInactive },
          { label: 'Fees', value: `$${this.report.totalFees}` }
        ];
        this.loading = false;
      },
      error: err => {
        console.error(err);
        this.loading = false;
        this.errorMessage = 'Failed to load report. Please try again.';
      }
    });
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.dataSvc.exportContractorSummaryReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contractor-summary-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contractor-summary-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (event.sortField === null) {
      // reset to default sorting
      this.sortField = 'registrationType'; // choose your default
      this.sortDescending = false;
    } else {
      this.sortField = event.sortField;
      this.sortDescending = event.sortDescending ?? false;
    }
    this.page.pageNumber = 0;
    this.getReport();
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.page.size = pageSize;
    this.page.pageNumber = 0;
    this.getReport();
  }

  onPageChange(page: number) {
    this.page.pageNumber = page;
    this.getReport();
  }

  onRetry() {
    this.getReport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sortField'] || changes['sortDescending']) {
      if (
        !changes['sortField']?.firstChange ||
        !changes['sortDescending']?.firstChange
      ) {
        this.getReport();
      }
    }
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  onFiltersChanged(filters: FilterBuilderOutput) {
    this.filters = filters;
    this.page.pageNumber = 0; // reset to first page
    this.getReport();
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.key)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }
}
